// S3 (pestaña) — Correcciones de paro propuestas por el técnico de
// mantenimiento. El supervisor las aprueba (el server aplica el UPDATE al paro
// y recalcula minutos_perdidos) o las rechaza con razón obligatoria.
//
// Vive como pestaña dentro de S3BandejaIncidenciasPage (mismo look: filtros
// SegmentedControl + cards). Los datetimes del backend vienen en "fake-UTC"
// (hora local del tenant serializada como UTC) → se formatean SIEMPRE con
// timeZone: 'UTC' para no re-desplazarlos al huso del navegador.

import { useState } from 'react';
import { toast } from 'sonner';
import type { CorreccionParoBandejaItem, EstadoValidacionParo } from 'schemas';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { SegmentedControl } from '../../components/ui/segmented-control';
import { Spinner } from '../../components/ui/spinner';
import { Textarea } from '../../components/ui/textarea';
import { ApiError } from '../../lib/api/client';
import { cn } from '../../lib/utils';
import {
  useAprobarCorreccionParo,
  useCorreccionesParo,
  useRechazarCorreccionParo,
} from './correccionesParoQueries';

// =============================================================================
// Helpers de formato
// =============================================================================

/** Hora HH:MM de un ISO fake-UTC (hora local del tenant guardada como UTC). */
const fmtHora = (iso: string | null): string =>
  iso === null
    ? '—'
    : new Date(iso).toLocaleTimeString('es-CO', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC',
      });

/** "2026-07-06" → "06/07". */
const fmtFecha = (fecha: string): string => {
  const [, m, d] = fecha.split('-');
  return `${d}/${m}`;
};

const fmtFechaHora = (iso: string): string => {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} ${String(
    d.getHours(),
  ).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const fmtMinutos = (min: number): string =>
  min >= 60 ? `${Math.floor(min / 60)}h ${min % 60}m` : `${min}m`;

/** Mensajes humanos para los códigos de negocio del PATCH aprobar/rechazar. */
function mensajeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.code === 'cobertura_invalida')
      return 'No se puede aprobar: dejaría un bloque con producción 0 sin paro total.';
    if (err.code === 'invalid_state') return 'Esta corrección ya fue resuelta.';
    if (err.code === 'hora_fin_futura') return 'La hora fin propuesta está en el futuro.';
    return err.message;
  }
  return err instanceof Error ? err.message : fallback;
}

// =============================================================================
// Pestaña
// =============================================================================

export function S3CorreccionesParoTab() {
  const [estado, setEstado] = useState<EstadoValidacionParo>('propuesta');
  const [aAprobar, setAAprobar] = useState<CorreccionParoBandejaItem | null>(null);
  const [aRechazar, setARechazar] = useState<CorreccionParoBandejaItem | null>(null);
  const { data, isLoading, isError, error, isFetching } = useCorreccionesParo(estado);

  const items = data?.items ?? [];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3 border-b border-border pb-3">
        <div>
          <h1 className="text-xl font-medium text-fg">Correcciones de paros</h1>
          <p className="text-sm text-fg-tertiary">
            Propuestas del técnico de mantenimiento sobre paros ya registrados. Al aprobar, el
            cambio se aplica al paro y queda auditado; para rechazar debe indicar la razón.
          </p>
        </div>
        {isFetching ? <Spinner className="mb-1 size-4" /> : null}
      </header>

      <SegmentedControl<EstadoValidacionParo>
        value={estado}
        onChange={setEstado}
        options={[
          {
            value: 'propuesta',
            label: `Propuestas${data ? ` (${data.conteos.propuestas})` : ''}`,
          },
          {
            value: 'aprobada',
            label: `Aprobadas${data ? ` (${data.conteos.aprobadas})` : ''}`,
          },
          {
            value: 'rechazada',
            label: `Rechazadas${data ? ` (${data.conteos.rechazadas})` : ''}`,
          },
        ]}
      />

      {isLoading ? (
        <div className="flex items-center justify-center gap-2 py-12 text-sm text-fg-tertiary">
          <Spinner className="size-5" /> Cargando correcciones…
        </div>
      ) : isError ? (
        <p className="py-8 text-center text-sm text-crit">
          {error instanceof Error ? error.message : 'No pudimos cargar las correcciones'}
        </p>
      ) : items.length === 0 ? (
        <div className="rounded border border-dashed border-border p-8 text-center text-sm text-fg-tertiary">
          {estado === 'propuesta'
            ? 'Sin correcciones propuestas por resolver. Al día. ✓'
            : `Sin correcciones ${estado === 'aprobada' ? 'aprobadas' : 'rechazadas'}.`}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {items.map((c) => (
            <CorreccionCard
              key={c.id}
              c={c}
              onAprobar={() => setAAprobar(c)}
              onRechazar={() => setARechazar(c)}
            />
          ))}
        </div>
      )}

      {aAprobar ? <AprobarDialog c={aAprobar} onClose={() => setAAprobar(null)} /> : null}
      {aRechazar ? <RechazarDialog c={aRechazar} onClose={() => setARechazar(null)} /> : null}
    </div>
  );
}

// =============================================================================
// Card de corrección — contexto + razón + diff actual → propuesto
// =============================================================================

function CorreccionCard({
  c,
  onAprobar,
  onRechazar,
}: {
  c: CorreccionParoBandejaItem;
  onAprobar: () => void;
  onRechazar: () => void;
}) {
  const cambiaCausal = c.propuesto.causal_madre !== null || c.propuesto.causal !== null;
  const cambiaHoras = c.propuesto.hora_inicio !== null || c.propuesto.hora_fin !== null;

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-4 pt-4 text-sm">
        {/* Header: contexto operativo + estado */}
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="text-fg">
            <span className="font-medium">{c.linea_nombre}</span>{' '}
            <span className="text-fg-tertiary">({c.planta_nombre})</span>
            <span className="mx-1.5 text-fg-tertiary">·</span>
            <span className="tabular-nums text-fg-secondary">{fmtFecha(c.fecha)}</span>
            {c.turno_nombre ? <span className="text-fg-secondary"> · {c.turno_nombre}</span> : null}
          </p>
          <span className="flex items-center gap-2 text-[11px] text-fg-tertiary">
            {c.estado === 'aprobada' ? <Badge tone="ok">Aprobada</Badge> : null}
            {c.estado === 'rechazada' ? <Badge tone="crit">Rechazada</Badge> : null}
            propuesta {fmtFechaHora(c.created_at)}
          </span>
        </div>

        {/* Quién propone / quién registró */}
        <p className="-mt-1 text-xs text-fg-tertiary">
          Propone: {c.tecnico.nombre} {c.tecnico.apellido} (mantenimiento)
          <span className="mx-1.5">·</span>
          Registró: {c.lider ? `${c.lider.nombre} ${c.lider.apellido}` : '—'}
        </p>

        {/* Razón del técnico */}
        <div className="rounded border border-border bg-surface-overlay/40 px-3 py-2">
          <p className="text-[10px] font-medium uppercase tracking-wide text-fg-tertiary">
            Razón de la corrección
          </p>
          <p className="mt-0.5 text-fg">{c.razon}</p>
        </div>

        {/* Diff actual → propuesto */}
        <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-3">
          <DiffFila label="Causal">
            <span className={cn(cambiaCausal ? 'text-fg-tertiary line-through' : 'text-fg')}>
              {c.actual.causal_madre.nombre} · {c.actual.causal.nombre}
            </span>
            {cambiaCausal ? (
              <>
                <FlechaDiff />
                <span className="font-medium text-accent">
                  {(c.propuesto.causal_madre ?? c.actual.causal_madre).nombre} ·{' '}
                  {(c.propuesto.causal ?? c.actual.causal).nombre}
                </span>
              </>
            ) : (
              <SinCambio />
            )}
          </DiffFila>

          <DiffFila label="Horario">
            <span
              className={cn(
                'tabular-nums',
                cambiaHoras ? 'text-fg-tertiary line-through' : 'text-fg',
              )}
            >
              {fmtHora(c.actual.hora_inicio)}–{fmtHora(c.actual.hora_fin)}
            </span>
            {cambiaHoras ? (
              <>
                <FlechaDiff />
                <span className="font-medium tabular-nums text-accent">
                  {fmtHora(c.propuesto.hora_inicio ?? c.actual.hora_inicio)}–
                  {fmtHora(c.propuesto.hora_fin ?? c.actual.hora_fin)}
                </span>
              </>
            ) : (
              <SinCambio />
            )}
          </DiffFila>

          <DiffFila label="Minutos">
            <DiffMinutos actual={c.actual.minutos} propuesto={c.propuesto.minutos_resultantes} />
          </DiffFila>
        </div>

        {/* Acciones (solo propuesta) / resolución (aprobada|rechazada) */}
        {c.estado === 'propuesta' ? (
          <div className="flex justify-end gap-2 border-t border-border/60 pt-3">
            <Button size="sm" variant="outline" onClick={onRechazar}>
              Rechazar
            </Button>
            <Button size="sm" onClick={onAprobar}>
              Aprobar
            </Button>
          </div>
        ) : (
          <div className="rounded border border-border px-3 py-2 text-xs text-fg-secondary">
            <p className="font-medium text-fg">
              {c.estado === 'aprobada' ? '✓ Aprobada' : 'Rechazada'}
              {c.resuelto_at ? ` · ${fmtFechaHora(c.resuelto_at)}` : ''}
            </p>
            {c.razon_resolucion ? <p className="mt-1">{c.razon_resolucion}</p> : null}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DiffFila({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] font-medium uppercase tracking-wide text-fg-tertiary">
        {label}
      </span>
      <span className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">{children}</span>
    </div>
  );
}

function FlechaDiff() {
  return (
    <span aria-hidden className="text-fg-tertiary">
      →
    </span>
  );
}

function SinCambio() {
  return <span className="text-[11px] text-fg-tertiary">(sin cambio)</span>;
}

/** Minutos actual → resultantes, con delta coloreado (más = crit, menos = ok). */
function DiffMinutos({ actual, propuesto }: { actual: number | null; propuesto: number | null }) {
  if (propuesto === null || actual === propuesto) {
    return (
      <>
        <span className="tabular-nums text-fg">{actual !== null ? fmtMinutos(actual) : '—'}</span>
        <SinCambio />
      </>
    );
  }
  const delta = actual !== null ? propuesto - actual : null;
  const deltaClass = delta !== null && delta > 0 ? 'text-crit' : 'text-ok';
  return (
    <>
      <span className="tabular-nums text-fg-tertiary line-through">
        {actual !== null ? fmtMinutos(actual) : '—'}
      </span>
      <FlechaDiff />
      <span className="font-medium tabular-nums text-accent">{fmtMinutos(propuesto)}</span>
      {delta !== null ? (
        <span className={cn('text-[11px] font-medium tabular-nums', deltaClass)}>
          ({delta > 0 ? '+' : ''}
          {delta} min)
        </span>
      ) : null}
    </>
  );
}

// =============================================================================
// Aprobar — confirmación con razón OPCIONAL (si se escribe, mín. 10)
// =============================================================================

function AprobarDialog({ c, onClose }: { c: CorreccionParoBandejaItem; onClose: () => void }) {
  const aprobar = useAprobarCorreccionParo();
  const [razon, setRazon] = useState('');

  const trimmed = razon.trim();
  const razonValida = trimmed.length === 0 || trimmed.length >= 10;

  const confirmar = async () => {
    try {
      const r = await aprobar.mutateAsync({
        id: c.id,
        input: trimmed.length > 0 ? { razon_resolucion: trimmed } : {},
      });
      toast.success(
        'Corrección aprobada — el cambio quedó aplicado al paro',
        typeof r.minutos_perdidos === 'number'
          ? { description: `El paro queda con ${fmtMinutos(r.minutos_perdidos)} perdidos.` }
          : undefined,
      );
      onClose();
    } catch (err) {
      toast.error(mensajeError(err, 'No pudimos aprobar la corrección'));
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Aprobar corrección</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3 text-sm">
          <p className="text-fg-secondary">
            El paro de <strong className="text-fg">{c.linea_nombre}</strong> ({fmtFecha(c.fecha)})
            se actualiza con los valores propuestos y el cambio queda auditado.
          </p>
          <div className="flex flex-col gap-1">
            <Label htmlFor="cp-aprobar-razon">Comentario (opcional, mín. 10 caracteres)</Label>
            <Textarea
              id="cp-aprobar-razon"
              rows={2}
              value={razon}
              onChange={(e) => setRazon(e.target.value)}
              placeholder="Ej.: verificado con el líder del turno"
            />
            {trimmed.length > 0 ? (
              <p className={cn('text-xs', razonValida ? 'text-ok' : 'text-fg-tertiary')}>
                {trimmed.length} / 10 caracteres mínimos
              </p>
            ) : null}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} disabled={aprobar.isPending}>
              Cancelar
            </Button>
            <Button
              disabled={!razonValida || aprobar.isPending}
              loading={aprobar.isPending}
              onClick={() => void confirmar()}
            >
              Aprobar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Rechazar — razón OBLIGATORIA (mín. 10) con contador
// =============================================================================

function RechazarDialog({ c, onClose }: { c: CorreccionParoBandejaItem; onClose: () => void }) {
  const rechazar = useRechazarCorreccionParo();
  const [razon, setRazon] = useState('');

  const trimmed = razon.trim();
  const valida = trimmed.length >= 10;

  const confirmar = async () => {
    try {
      await rechazar.mutateAsync({ id: c.id, input: { razon_resolucion: trimmed } });
      toast.success('Corrección rechazada — el técnico puede volver a proponer');
      onClose();
    } catch (err) {
      toast.error(mensajeError(err, 'No pudimos rechazar la corrección'));
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Rechazar corrección</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3 text-sm">
          <p className="text-fg-secondary">
            El paro de <strong className="text-fg">{c.linea_nombre}</strong> ({fmtFecha(c.fecha)})
            queda como está. La razón se comparte con el técnico.
          </p>
          <div className="flex flex-col gap-1">
            <Label htmlFor="cp-rechazar-razon">Razón del rechazo (mín. 10 caracteres)</Label>
            <Textarea
              id="cp-rechazar-razon"
              rows={3}
              value={razon}
              onChange={(e) => setRazon(e.target.value)}
              placeholder="Ej.: la causal actual es correcta según el líder del turno"
            />
            <p className={cn('text-xs', valida ? 'text-ok' : 'text-fg-tertiary')}>
              {trimmed.length} / 10 caracteres mínimos
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} disabled={rechazar.isPending}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              disabled={!valida || rechazar.isPending}
              loading={rechazar.isPending}
              onClick={() => void confirmar()}
            >
              Rechazar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
